import api from './utils/api.js';
import { t } from './i18n.js';

// Safe player defaults to prevent null access
const PLAYER_DEFAULTS = Object.freeze({
  health: 0, 
  thirst: 0, 
  hunger: 0, 
  energy: 0,
  coins: 0, 
  money: 0, 
  xp: 0, 
  level: 1,
  skillPoints: 0, 
  stats: { strength: 0, agility: 0, intellect: 0 },
  location: null, 
  lastDailyAt: null,
  id: null,
  cityId: 'lumina'
});

export const gameStore = {
  // Player stats (synced with API)
  player: {
    health: 100,
    thirst: 85,
    hunger: 70,
    energy: 100,
    coins: 1250,
    cityId: 'lumina',
    id: null
  },

  // User data (extracted from backend or JWT)
  user: null,
  userRole: null,
  isAdmin: false,

  // Authentication state
  auth: {
    isAuthenticated: false,
    user: null,
    token: null,
    loading: false,
    email: '',
    password: ''
  },
  
  // UI/View state
  view: {
    current: 'auth', // 'auth' | 'game'
    set(newView) {
      this.current = newView;
    }
  },

  // Game state
  game: {
    isPaused: false,
    lastUpdate: Date.now(),
    tickRate: 1000, // 1 second
    version: '0.1.0'
  },
  
  // UI state
  ui: {
    activeTab: 'missions',
    notifications: [],
    modals: {
      settings: false,
      inventory: false
    }
  },
  
  // World state
  world: {
    time: {
      hour: 12,
      day: 1,
      season: 'spring'
    },
    weather: 'sunny',
    temperature: 22
  },
  
  // Timers and ongoing activities
  timers: {
    mission: null,
    travel: null,
    crafting: null,
    rest: null
  },
  
  // Inventory
  inventory: {
    items: [
      { id: 1, name: 'Bread', quantity: 3, type: 'food' },
      { id: 2, name: 'Water Bottle', quantity: 2, type: 'drink' },
      { id: 3, name: 'Health Potion', quantity: 1, type: 'potion' }
    ],
    maxWeight: 100,
    currentWeight: 15
  },
  
  // Available missions
  missions: {
    available: [
      {
        id: 1,
        title: 'Gather Wood',
        description: 'Collect 10 pieces of wood from the forest',
        duration: 300, // 5 minutes in seconds
        rewards: { money: 50, experience: 25 },
        requirements: { level: 1 }
      },
      {
        id: 2,
        title: 'Deliver Package',
        description: 'Take this package to the merchant in the next town',
        duration: 600, // 10 minutes
        rewards: { money: 100, experience: 50 },
        requirements: { level: 2 }
      }
    ],
    active: null,
    completed: []
  },
  
  // Travel system
  travel: {
    destinations: [
      {
        id: 'forest',
        name: 'Dark Forest',
        description: 'A mysterious forest full of resources',
        travelTime: 180, // 3 minutes
        cost: 10
      },
      {
        id: 'mountains',
        name: 'Crystal Mountains',
        description: 'High peaks with rare crystals',
        travelTime: 900, // 15 minutes
        cost: 50
      }
    ],
    current: null
  },
  
  // Settings
  settings: {
    soundEnabled: true,
    musicEnabled: true,
    notifications: true,
    autoSave: true,
    theme: 'dark'
  },
  
  // Authentication methods
  async login(email, password) {
    this.auth.loading = true;
    try {
      const response = await api.login(email, password);
      
      this.auth.isAuthenticated = true;
      this.auth.user = response.user;
      this.auth.token = response.token;
      
      // Set user and role immediately from login response
      this.setUserAndRole(response.user);
      
      // Update player data from API
      if (response.player) {
        this.updatePlayerFromAPI(response.player);
      }
      
      // Set view to game after successful login
      this.view.set('game');
      
      console.log('Login successful');
      this.addNotification('¡Bienvenido de vuelta!', 'success');
      
      // Resolve user role with resilient fallback
      this.resolveUserRoleResilient();
      
      return response;
    } catch (error) {
      console.error('Login failed:', error);
      this.addNotification(error.message || 'Error al iniciar sesión', 'error');
      throw error;
    } finally {
      this.auth.loading = false;
    }
  },

  async register(email, password, username) {
    this.auth.loading = true;
    try {
      const response = await api.register(email, password, username);
      
      this.auth.isAuthenticated = true;
      this.auth.user = response.user;
      this.auth.token = response.token;
      
      // Update player data from API
      if (response.player) {
        this.updatePlayerFromAPI(response.player);
      }
      
      // Set view to game after successful registration
      this.view.set('game');
      
      console.log('Registration successful');
      this.addNotification('¡Cuenta creada exitosamente!', 'success');
      
      return response;
    } catch (error) {
      console.error('Registration failed:', error);
      this.addNotification(error.message || 'Error al crear cuenta', 'error');
      throw error;
    } finally {
      this.auth.loading = false;
    }
  },

  logout() {
    try {
      localStorage.removeItem('auth:token');
      this.auth.token = null;
      this.auth.user = null;
      this.auth.isAuthenticated = false;
      this.user = null;
      this.userRole = null;
      this.isAdmin = false;
      this.player = null;
      this.auth.email = '';
      this.auth.password = '';
      
      // Reset UI to auth tab to prevent player null access
      this.ui.activeTab = 'auth';
      
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'info', text: 'Sesión cerrada' }}));
      window.dispatchEvent(new CustomEvent('auth:role', { detail: { role: null, isAdmin: false } }));
      
      // Forzar UI a mostrar pantalla de login
      if (this.view && typeof this.view.set === 'function') {
        this.view.set('auth');
      }
      
      // Allow Alpine to reprocess before any other init tries to access player
      requestAnimationFrame(() => { 
        document.activeElement?.blur?.(); 
      });
      
      // Emit cleanup event for components with timers
      window.dispatchEvent(new CustomEvent('app:logout'));
    } catch {}
  },

  // Player data methods
  async refreshPlayer() {
    if (!this.auth.isAuthenticated) return;
    
    try {
      const response = await api.getPlayerProfile();
      this.updatePlayerFromAPI(response.player);
      
      // Handle offline progress notifications
      if (response.offlineProgress) {
        this.processOfflineProgress(response.offlineProgress);
      }
    } catch (error) {
      console.error('Failed to refresh player data:', error);
    }
  },

  // Process offline progress and show notifications
  processOfflineProgress(offlineProgress) {
    const { missionsCompleted, travelsCompleted, totalXpGained, totalLevelsGained } = offlineProgress;
    
    // Show mission completion notifications
    missionsCompleted.forEach(mission => {
      let message = `✅ Misión "${mission.missionName}" completada`;
      if (mission.xpGained > 0) {
        message += ` (+${mission.xpGained} XP)`;
      }
      if (mission.levelsGained > 0) {
        message += ` 🎉 ¡Subiste ${mission.levelsGained} nivel(es)!`;
      }
      this.addNotification(message, 'success');
    });
    
    // Show travel completion notifications
    travelsCompleted.forEach(travel => {
      this.addNotification(
        `🚶 Llegaste a ${travel.toCityName} desde ${travel.fromCityName}`,
        'info'
      );
    });
    
    // Show summary if there were multiple changes
    if (missionsCompleted.length > 1 || travelsCompleted.length > 1) {
      let summary = '📋 Progreso offline: ';
      const parts = [];
      
      if (missionsCompleted.length > 0) {
        parts.push(`${missionsCompleted.length} misiones completadas`);
      }
      if (travelsCompleted.length > 0) {
        parts.push(`${travelsCompleted.length} viajes finalizados`);
      }
      if (totalXpGained > 0) {
        parts.push(`+${totalXpGained} XP total`);
      }
      
      summary += parts.join(', ');
      this.addNotification(summary, 'success');
    }
  },

  updatePlayerFromAPI(playerData) {
    const stats = playerData.stats || {};
    
    this.player = {
      id: playerData.id,
      health: stats.health || 100,
      thirst: stats.thirst || 85,
      hunger: stats.hunger || 70,
      energy: stats.energy || 100,
      strength: stats.strength || 1,
      agility: stats.agility || 1,
      intellect: stats.intellect || 1,
      coins: playerData.coins || 0,
      xp: playerData.xp || 0,
      level: playerData.level || 1,
      skillPoints: playerData.skillPoints || 0,
      cityId: playerData.cityId || 'lumina',
      lastActiveAt: playerData.lastActiveAt,
      lastDailyAt: playerData.lastDailyAt,
      stats: stats // Keep full stats object for components
    };
  },

  // Methods for updating state
  updatePlayer(updates) {
    Object.assign(this.player, updates);
  },

  async syncPlayerStats() {
    if (!this.auth.isAuthenticated) return;
    
    try {
      const stats = {
        health: this.player.health,
        thirst: this.player.thirst,
        hunger: this.player.hunger,
        energy: this.player.energy
      };
      
      await api.updatePlayerStats(stats);
    } catch (error) {
      console.error('Failed to sync player stats:', error);
    }
  },

  // Set user data and derived properties
  setUserAndRole(user) {
    this.user = user;
    this.userRole = user?.role || null;
    this.isAdmin = user?.role === 'ADMIN';
    
    // Emit event for reactive UI components
    window.dispatchEvent(new CustomEvent('auth:role', {
      detail: { role: this.userRole, isAdmin: this.isAdmin }
    }));
  },

  // JWT decode helper (Base64)
  decodeJWT(token) {
    try {
      const payload = token.split('.')[1];
      const decoded = JSON.parse(atob(payload));
      
      // Check expiration
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        return null; // Token expired
      }
      
      return decoded;
    } catch (error) {
      console.warn('Failed to decode JWT:', error);
      return null;
    }
  },

  // Resilient user role resolution
  async resolveUserRoleResilient() {
    const token = localStorage.getItem('auth:token');
    if (!token) return;

    // Retry configuration
    const maxRetries = 3;
    const delays = [300, 800, 1500];
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        if (import.meta.env.VITE_DEBUG_API === 'true') {
          console.log(`[AUTH] Attempting to resolve user role (attempt ${attempt + 1}/${maxRetries})`);
        }
        
        const response = await api.getProfile();
        
        if (response?.user) {
          this.setUserAndRole(response.user);
          
          if (import.meta.env.VITE_DEBUG_API === 'true') {
            console.log(`[AUTH] Role resolved successfully: ${response.user.role}`);
          }
          
          return response.user;
        }
      } catch (error) {
        if (import.meta.env.VITE_DEBUG_API === 'true') {
          console.log(`[FETCH_ME_RETRY] Attempt ${attempt + 1} failed, delay: ${delays[attempt]}ms`, error.message);
        }
        
        // If this is the last attempt, try JWT fallback
        if (attempt === maxRetries - 1) {
          const jwtPayload = this.decodeJWT(token);
          if (jwtPayload && jwtPayload.role) {
            const fallbackUser = {
              id: jwtPayload.userId,
              email: jwtPayload.email,
              role: jwtPayload.role
            };
            
            this.setUserAndRole(fallbackUser);
            
            if (import.meta.env.VITE_DEBUG_API === 'true') {
              console.log(`[AUTH] Role resolved from JWT fallback: ${jwtPayload.role}`);
            }
            
            return fallbackUser;
          }
          break;
        }
        
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, delays[attempt]));
      }
    }
    
    console.warn('[AUTH] Failed to resolve user role after all attempts');
    return null;
  },

  // Initialize authentication on app load
  async initializeAuth() {
    const token = localStorage.getItem('auth:token');
    if (!token) {
      return false;
    }

    this.auth.loading = true;
    try {
      // Set token in API client
      api.setToken(token);
      
      // Try JWT decode first for immediate role availability
      const jwtPayload = this.decodeJWT(token);
      if (jwtPayload) {
        const fallbackUser = {
          id: jwtPayload.userId,
          email: jwtPayload.email,
          role: jwtPayload.role
        };
        this.setUserAndRole(fallbackUser);
      }
      
      // Verify token with server (resilient)
      const user = await this.resolveUserRoleResilient();
      
      if (user) {
        this.auth.isAuthenticated = true;
        this.auth.user = user;
        this.auth.token = token;
        
        // Try to get player data
        try {
          const response = await api.getPlayerProfile();
          if (response?.player) {
            this.updatePlayerFromAPI(response.player);
          }
        } catch (playerError) {
          console.warn('Could not load player data:', playerError.message);
        }
        
        // Set view to game if authenticated
        this.view.set('game');
        
        console.log('Authentication restored');
        return true;
      } else {
        throw new Error('Could not resolve user data');
      }
    } catch (error) {
      console.error('Failed to restore authentication:', error);
      // Clear invalid token
      localStorage.removeItem('auth:token');
      api.clearAuth();
      this.view.set('auth');
      return false;
    } finally {
      this.auth.loading = false;
    }
  },

  // Start periodic sync with API
  startPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    this.syncInterval = setInterval(async () => {
      if (this.auth.isAuthenticated) {
        await this.refreshPlayer();
      }
    }, 30000); // Sync every 30 seconds
  },

  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  },
  
  updateUI(updates) {
    Object.assign(this.ui, updates);
  },
  
  setActiveTab(tab) {
    this.ui.activeTab = tab;
  },
  
  // Timer management
  setTimer(type, endTime, callback) {
    this.timers[type] = {
      endTime,
      callback,
      startTime: Date.now()
    };
  },
  
  clearTimer(type) {
    this.timers[type] = null;
  },
  
  // Add notification to queue (use toast system)
  addNotification(message, type = 'info') {
    // Use new toast system
    window.dispatchEvent(new CustomEvent('show-toast', {
      detail: { message, type, duration: 5000 }
    }));
    
    console.log(`Notification added: ${type} - ${message}`);
  },
  
  removeNotification(id) {
    const index = this.ui.notifications.findIndex(n => n.id === id);
    if (index > -1) {
      this.ui.notifications.splice(index, 1);
    }
  },
  
  // Game save/load
  saveGame() {
    try {
      const saveData = {
        player: this.player,
        world: this.world,
        inventory: this.inventory,
        missions: this.missions,
        settings: this.settings,
        timestamp: Date.now()
      };
      localStorage.setItem('destinyReborn_save', JSON.stringify(saveData));
    } catch (error) {
      console.error('Failed to save game:', error);
    }
  },
  
  loadGame() {
    try {
      const saveData = localStorage.getItem('destinyReborn_save');
      if (saveData) {
        const parsed = JSON.parse(saveData);
        
        // Merge saved data with current state
        Object.assign(this.player, parsed.player || {});
        Object.assign(this.world, parsed.world || {});
        Object.assign(this.inventory, parsed.inventory || {});
        Object.assign(this.missions, parsed.missions || {});
        Object.assign(this.settings, parsed.settings || {});
        
        console.log('Game loaded successfully');
        return true;
      }
    } catch (error) {
      console.error('Failed to load game:', error);
    }
    return false;
  },
  
  // Reset game
  resetGame() {
    localStorage.removeItem('destinyReborn_save');
    window.location.reload();
  }
};

// Auto-load game on initialization
gameStore.loadGame();

// Setup event listeners for external state changes
window.addEventListener('missions:state-changed', (event) => {
  // Update missions state in store when missions service changes
  try {
    const missionsState = JSON.parse(localStorage.getItem('missions:state') || '{}');
    if (missionsState.activeRun) {
      // Sync active mission state
      gameStore.missions.active = missionsState.activeRun;
    } else {
      gameStore.missions.active = null;
    }
    
    // Update completed count
    if (missionsState.completedRuns) {
      gameStore.missions.completed = missionsState.completedRuns;
    }
    
    console.log('Missions state synchronized with service');
  } catch (error) {
    console.warn('Failed to sync missions state:', error);
  }
});

// Add getText method to gameStore for i18n
gameStore.getText = function(key, fallback = null) {
  return t(key, fallback);
};

// Safe getters to prevent null access crashes
gameStore.hasPlayer = function() { 
  return !!this.player; 
};

gameStore.p = function() { 
  return this.player ?? PLAYER_DEFAULTS; 
};

// Auto-logout listener
window.addEventListener('app:logout', () => {
  if (window.Alpine?.store('app')?.logout) {
    window.Alpine.store('app').logout();
  }
});